import { SELECTS_REQUIRED_KINDS, type ProjectPhase } from '../../../shared/types';
import { formatDayMonth, formatWeekdayDate, type SortOption, type SortValue } from '../../components/primitives';
import type { CardProject, View } from './types';

// ── Sorting (feedback r2 §1) ─────────────────────────────────────────────
// One comparator drives both the Active shelves and the Delivered/All
// lists, so the button label always describes what is on screen.
export const SORT_OPTIONS: SortOption[] = [
  { key: 'number', label: 'Number', natural: 'desc', naturalLabel: 'newest first' },
  { key: 'next', label: 'Next date', natural: 'asc', naturalLabel: 'soonest first' },
  { key: 'due', label: 'Due date', natural: 'asc', naturalLabel: 'soonest first' },
  { key: 'delivered', label: 'Delivered date', natural: 'desc', naturalLabel: 'newest first' },
  { key: 'client', label: 'Client', natural: 'asc', naturalLabel: 'A–Z' },
  { key: 'name', label: 'Name', natural: 'asc', naturalLabel: 'A–Z' },
];

/** Keys compared as text (case-insensitively) rather than number/ISO date. */
const TEXT_SORT_KEYS = new Set(['client', 'name']);

/** Each tab opens on the order that tab is for. */
export function defaultSortFor(view: View): SortValue {
  if (view === 'delivered') return { key: 'delivered', dir: 'desc' };
  if (view === 'all') return { key: 'number', dir: 'desc' };
  // Clean up: the longest-outstanding delivery first.
  if (view === 'clean_up') return { key: 'delivered', dir: 'asc' };
  return { key: 'next', dir: 'asc' };
}

function sortKeyOf(p: CardProject, key: string): string | number | null {
  switch (key) {
    case 'number': return p.project_number;
    case 'next': return p.shoot_date ?? p.due_date;
    case 'due': return p.due_date;
    case 'delivered': return p.delivered_date;
    case 'client': return p.client_name || null;
    case 'name': return p.name || null;
    default: return null;
  }
}

/** Missing values (unnumbered, undated, no client) always sink, whichever
    way the arrow points; ties fall back to newest-created first. */
export function compareProjects(sort: SortValue) {
  const flip = sort.dir === 'asc' ? 1 : -1;
  const text = TEXT_SORT_KEYS.has(sort.key);
  return (a: CardProject, b: CardProject) => {
    const ka = sortKeyOf(a, sort.key);
    const kb = sortKeyOf(b, sort.key);
    if (ka === null || kb === null) {
      if (ka !== null) return -1;
      if (kb !== null) return 1;
    } else {
      const cmp = text
        ? String(ka).localeCompare(String(kb), undefined, { sensitivity: 'base' })
        : ka < kb ? -1 : ka > kb ? 1 : 0;
      if (cmp !== 0) return cmp * flip;
    }
    return b.created_at - a.created_at;
  };
}

export function serializeSort(sort: SortValue): string {
  return `${sort.key}:${sort.dir}`;
}

export function parseSort(raw: string | undefined): SortValue | null {
  if (!raw) return null;
  const [key, dir] = raw.split(':');
  if (!key || !SORT_OPTIONS.some((o) => o.key === key)) return null;
  if (dir !== 'asc' && dir !== 'desc') return null;
  return { key, dir };
}

export function pillTone(phase: ProjectPhase): 'accent' | 'gold' | 'cleanup' | undefined {
  if (phase === 'clean_up') return 'cleanup';
  return phase === 'post' || phase === 'delivered' ? 'accent' : phase === 'shoot' ? 'gold' : undefined;
}

export function cardDate(p: CardProject, today: string): { text: string; hot?: boolean } {
  if (p.phase === 'on_hold') {
    const d = new Date(p.updated_at);
    return { text: `Paused ${d.getDate()} ${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()]}` };
  }
  if (p.phase === 'shoot' && p.shoot_date) {
    const soon = p.shoot_date >= today && p.shoot_date <= addDaysIso(today, 7);
    return { text: `Shoot ${formatWeekdayDate(p.shoot_date)}`, hot: soon };
  }
  if ((p.phase === 'pre-production' || p.phase === 'shoot') && p.shoot_date) {
    return { text: `Shoot ${formatDayMonth(p.shoot_date)}` };
  }
  if (p.phase === 'lead' && !p.due_date) return { text: 'Kickoff TBC' };
  if (p.due_date) return { text: `Due ${formatDayMonth(p.due_date)}` };
  return { text: '—' };
}

export function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function waitingStatus(p: CardProject, today: string): string {
  if (p.waiting_snoozed_until && p.waiting_snoozed_until >= today) {
    return `Snoozed · ${formatDayMonth(p.waiting_snoozed_until)}`;
  }
  if (p.waiting_nudge_count >= 3) return 'Sent 3 of 3';
  const base = p.waiting_nudge_count === 0 ? p.waiting_on_client_since : p.waiting_last_nudge_at;
  const waitDays = p.waiting_nudge_count === 0 ? 4 : 7;
  const remaining = base === null ? waitDays : Math.max(0, Math.ceil((base + waitDays * 86_400_000 - Date.now()) / 86_400_000));
  if (p.waiting_nudge_count === 0) return remaining === 0 ? 'Scheduled — tonight' : `Scheduled · in ${remaining}d`;
  return `Sent ${p.waiting_nudge_count} of 3${remaining === 0 ? ' · next tonight' : ` · next in ${remaining}d`}`;
}

/** What still stands between a Clean up project and Closed (feedback round
    three): freelance selects not yet exported, and footage with no physical
    backup. Empty means it is ready to close. */
export function cleanupOutstanding(p: CardProject): ('selects' | 'backup')[] {
  const out: ('selects' | 'backup')[] = [];
  if (SELECTS_REQUIRED_KINDS.includes(p.project_kind) && !p.selects_exported_at) out.push('selects');
  if (!p.has_manual_backup && !p.no_footage) out.push('backup');
  return out;
}
